import React, { useEffect, useRef, ReactNode, MouseEvent } from 'react';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  maxWidthClass?: string;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * Accessible modal dialog built on the native HTML `<dialog>` element
 * using `.showModal()` and `.close()` for browser-managed focus trapping,
 * top-layer rendering, Escape handling, and `::backdrop` transitions.
 */
export function Dialog({
  open,
  onClose,
  title,
  subtitle,
  maxWidthClass = 'max-w-xl',
  children,
  footer,
}: DialogProps) {
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;

    if (open && !el.open) {
      try {
        el.showModal();
      } catch {
        el.setAttribute('open', '');
      }
    } else if (!open && el.open) {
      try {
        el.close();
      } catch {
        el.removeAttribute('open');
      }
    }
  }, [open]);

  const handleBackdropClick = (e: MouseEvent<HTMLDialogElement>) => {
    const el = dialogRef.current;
    if (!el) return;
    if (e.target === el) {
      onClose();
    }
  };

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      onClick={handleBackdropClick}
      className={`sync-dialog w-full ${maxWidthClass} m-auto`}
      aria-labelledby="dialog-title"
    >
      <div className="flex flex-col max-h-[calc(100dvh-3rem)]">
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4">
          <div>
            <h2 id="dialog-title" className="text-base font-semibold text-slate-900">
              {title}
            </h2>
            {subtitle && <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
            aria-label="Close modal"
          >
            ✕
          </button>
        </header>

        <div className="overflow-y-auto px-6 py-5 text-sm text-slate-700">{children}</div>

        {footer && (
          <footer className="flex items-center justify-end gap-2.5 border-t border-slate-200 bg-slate-50/70 px-6 py-3.5 rounded-b-[0.875rem]">
            {footer}
          </footer>
        )}
      </div>
    </dialog>
  );
}
